import { useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import {
  ReeducationsOptions,
  TextDifficulty,
  TitreChamp,
} from "@/data/ReeducationOptions";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Save, Wrench } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Selection } from "@/components/practitioner/champVisuel/ChampVisuelParameter";
import { Card } from "@/components/ui/card";
import { makeAdaptedData } from "@/utils/makeAdaptedData";

export interface TParams {
  content: string;
  sessionId: string;
}

const formSchema = z.object({
  parameters: z.object({
    ZF: z.object({
      sous_module: z.string(),
      selected: z.boolean(),
      nombre_cible: z.string(),
    }),
    ZPL: z.object({
      sous_module: z.string(),
      selected: z.boolean(),
      taille: z.array(z.string()),
      nombre_par_hemichamp: z.string(),
      couleur_cible: z.string(),
      couleur_fond: z.string(),
      side: z.string(),
      mouvement: z.string(),
      duree: z.string(),
      zt: z.string(),
    }),
    ZPI: z.object({
      sous_module: z.string(),
      selected: z.boolean(),
      type_forme: z.array(z.string()),
      taille: z.array(z.string()),
      nombre_par_hemichamp: z.string(),
      side: z.string(),
      mouvement: z.string(),
      couleur_cible: z.string(),
      couleur_fond: z.string(),
      duree: z.string(),
      zt: z.string(),
    }),
    ZPCCF: z.object({
      sous_module: z.string(),
      selected: z.boolean(),
      taille: z.array(z.string()),
      side_aveugle: z.string(),
      mouvement: z.string(),
      duree: z.string(),
      zt: z.string(),
    }),
    ZPFCF: z.object({
      sous_module: z.string(),
      selected: z.boolean(),
      couleur_cible: z.string(),
      couleur_fond: z.string(),
      taille: z.array(z.string()),
      type_forme: z.array(z.string()),
      mouvement: z.string(),
      duree: z.string(),
      zt: z.string(),
    }),
    OT: z.object({
      sous_module: z.string(),
      selected: z.boolean(),
      vitesse: z.string(),
      duree: z.string(),
      contenu: z.string(),
    }),
    OB: z.object({
      sous_module: z.string(),
      selected: z.boolean(),
      vitesse: z.string(),
      duree: z.string(),
      contenu: z.string(),
    }),
    PVS: z.object({
      sous_module: z.string(),
      selected: z.boolean(),
      vitesse: z.string(),
      duree: z.string(),
    }),
  }),
  notes: z.string().optional(),
  sujet: z.string().max(40).optional(),
  visualFieldSelection: z.string(),
});

export default function Reeducation() {
  const { patientId, sessionId } = useParams();
  const [selections, setSelections] = useState<Selection[]>([]);
  const [numberCasesZF, setLoadingNumberCasesZF] = useState<number>(0);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      parameters: {
        ZF: {
          sous_module: "ZF",
          selected: false,
          nombre_cible: "10",
        },
        ZPL: {
          sous_module: "ZPL",
          selected: false,
          taille: ["1"],
          nombre_par_hemichamp: "1",
          couleur_cible: "white",
          couleur_fond: "black",
          side: "unilatérale",
          mouvement: "true",
          duree: "15",
          zt: "Automatique",
        },
        ZPI: {
          taille: ["1"],
          sous_module: "ZPI",
          selected: false,
          type_forme: ["géométrique"],
          nombre_par_hemichamp: "1",
          side: "unilatérale",
          mouvement: "true",
          couleur_cible: "white",
          couleur_fond: "black",
          duree: "15",
          zt: "Automatique",
        },
        ZPCCF: {
          sous_module: "ZPCCF",
          selected: false,
          taille: ["1"],
          side_aveugle: "droit",
          mouvement: "true",
          duree: "15",
          zt: "Automatique",
        },
        ZPFCF: {
          sous_module: "ZPFCF",
          selected: false,
          taille: ["2"],
          couleur_cible: "white",
          couleur_fond: "black",
          type_forme: ["géométrique"],
          mouvement: "true",
          duree: "15",
          zt: "Automatique",
        },
        PVS: {
          sous_module: "PVS",
          selected: false,
          vitesse: "2",
          duree: "30",
        },
        OT: {
          sous_module: "OT",
          selected: false,
          vitesse: "4",
          duree: "30",
          contenu: "Le Chat et le perroquet - Sara Cone Bryant",
        },
        OB: {
          sous_module: "OB",
          selected: false,
          vitesse: "4",
          duree: "30",
          contenu: "Le Chat et le perroquet - Sara Cone Bryant",
        },
      },
      notes: "",
      sujet: "",
      visualFieldSelection: "Automatique",
    },
  });

  // Removed noisy debug effect that caused a lint warning for missing deps

  const visualFieldSelection = form.watch("visualFieldSelection");
  // Watch nombre_cible so estimation UI updates immediately on change
  const nombreCibleZF = form.watch("parameters.ZF.nombre_cible");
  useEffect(() => {
    if (patientId && visualFieldSelection !== "") {
      // Add a timestamp param to avoid stale cached responses in some prod proxies/CDNs
      axios
        .get(
          `/practitioner/visualfield/${patientId}/estimationTempsZF/${visualFieldSelection}`,
          { params: { _ts: Date.now() } }
        )
        .then((response) => {
          setLoadingNumberCasesZF(response.data);
        });
    } else {
      console.error(
        "PatientId ou visualFieldSelection manquant",
        patientId,
        visualFieldSelection
      );
    }
  }, [visualFieldSelection, patientId]);

  useEffect(() => {
    // 1) Récupérer la sélection
    axios
      .get(`/practitioner/visualfield/${patientId}/selection`)
      .then((respSel) => {
        setSelections(respSel.data);

        // 2) Puis récupérer la session en parallèle ou en série
        if (sessionId) {
          axios.get(`/practitioner/session/${sessionId}`).then((respSess) => {
            const sessionData = respSess.data;
            const adaptedData = makeAdaptedData(sessionData); // votre parsing

            // 3) On merge la sessionData ET la sélection si besoin
            //    par exemple, si la session n’a pas de champ selection, on peut la set par défaut
            if (!adaptedData.visualFieldSelection) {
              adaptedData.visualFieldSelection =
                respSel.data[0]?.timeline || "";
            }

            // 4) On reset le form UNE SEULE FOIS
            form.reset(adaptedData);
          });
        } else {
          // Pas de sessionId => juste la sélection
          // Step 1: Define the function to check for "Automatique" in timelines
          const hasZTPrincipaleTimeline = (
            data: { timeline: string }[]
          ): boolean => {
            return data.some((item) => item.timeline === "Automatique");
          };

          // Step 2: Use the function to set visualFieldSelection
          let visualFieldSelection;
          if (hasZTPrincipaleTimeline(respSel.data)) {
            visualFieldSelection = "Automatique";
          } else {
            visualFieldSelection = respSel.data[0]?.timeline || "";
          }
          form.setValue("visualFieldSelection", visualFieldSelection);
        }
      })
      .catch((err) => console.error(err));
  }, [patientId, sessionId, form]);

  const handleSubmite = (data: z.infer<typeof formSchema>) => {
    const selectedModules = Object.keys(data.parameters).filter(
      (key) => data.parameters[key as keyof typeof data.parameters].selected
    );
    const formData = {
      ...data,
      status: "PREVUE",
      type: selectedModules,
      sessionType : "REEDUCATION"
    };

    if (sessionId) {
      axios.put("/practitioner/session/" + sessionId, formData);
      toast.success("La séance a bien été modifiée");
    } else {
      axios.post("/practitioner/session/" + patientId, formData);
      toast.success("La séance a bien été créée");
    }
  };
  return (
    <Card className="pb-4 pr-6 pl-6 w-5/6">
      <Form {...form}>
        <form 
          onSubmit={form.handleSubmit(handleSubmite)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
        >
          {/* <div className="w-5/6 flex  flex-row justify-center mt-6 mb-6 align-center">
              <FormField
                control={form.control}
                name="visualFieldSelection"
                render={({ field }) => (
                  <FormItem>
                    <div className="text-left ml-1">
                      <FormLabel>Selection des zones à cibler</FormLabel>
                    </div>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selectionner la zone de travail" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {selections.map((option) => (
                          <SelectItem
                            key={option.timeline}
                            value={option.timeline}
                          >
                            {option.timeline}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="sujet"
                render={({ field }) => (
                  <FormItem>
                    <div className="text-left ml-1">
                      <FormLabel>Thème de la séance</FormLabel>
                    </div>
                    <FormControl>
                      <Input
                        placeholder="Thème de la séance"
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
          </div> */}
          <Table className="mt-4">
            <TableHeader>
              <TableRow>
                {/* <TableHead className="text-center"></TableHead>
                <TableHead>Exercice</TableHead>
                <TableHead className="text-left">Paramètres</TableHead>
                <TableHead>Zone de travail</TableHead> */}
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="bg-module-champ-header text-white hover:bg-module-champ-header/70">
                <TableCell>
                  <strong>Champ Visuel</strong>
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>
              <TableRow className="bg-module-champ-row">
                <TableCell className="font-medium">
                  <FormField
                    name="parameters.ZF.selected"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  ></FormField>
                </TableCell>
                {/* <TableCell className="text-left">
                {ReeducationsOptions.ZF.module}
              </TableCell> */}
                <TableCell className="text-left">
                  {ReeducationsOptions.ZF.sous_module}
                </TableCell>
                <TableCell>
                  <FormField
                    control={form.control}
                    name="parameters.ZF.nombre_cible"
                    render={({ field }) => (
                      <FormItem className="flex flex-col items-start">
                        <FormLabel>Nombre de cible par case</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="..."
                            {...field}
                            className="w-24"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-center font-semibold">
                    <span className="text-md">Estimation :</span>
                    <span className="ml-2 text-md font-bold">
                      {" "}
                      {numberCasesZF} cases ≈{" "}
                      {Math.ceil(
                        (numberCasesZF * 2.5 * Number(nombreCibleZF || 0)) /
                          60
                      )}{" "}
                      min
                    </span>
                  </div>
                </TableCell>
              </TableRow>
              <TableRow className="bg-module-champ-row">
                <TableCell className="font-medium">
                  <FormField
                    name="parameters.ZPL.selected"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  ></FormField>
                </TableCell>
                {/* <TableCell className="text-left">
                {ReeducationsOptions.ZPL.module}
              </TableCell> */}
                <TableCell className="text-left">
                  {ReeducationsOptions.ZPL.sous_module}
                </TableCell>
                <TableCell className="text-left">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline">
                        <span className="sr-only">Modifier</span>
                        <Wrench className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>
                          {ReeducationsOptions.ZPL.sous_module}
                        </DialogTitle>
                        <DialogDescription>
                          Paramétrage de l'exercice
                        </DialogDescription>
                      </DialogHeader>
                      <div className="flex flex-col items-left space-x-2">
                        <FormField
                          control={form.control}
                          name="parameters.ZPL.duree"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Durée</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selectionner la durée" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {ReeducationsOptions.ZPL.parametre.duree?.map(
                                    (option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="parameters.ZPL.taille"
                          render={() => (
                            <FormItem>
                              <FormLabel>Taille de la cible</FormLabel>
                              {ReeducationsOptions.ZPL.parametre.taille?.map(
                                (option) => (
                                  <FormField
                                    key={option.value}
                                    control={form.control}
                                    name="parameters.ZPL.taille"
                                    render={({ field }) => {
                                      return (
                                        <FormItem
                                          key={option.value}
                                          className="flex flex-row items-start space-x-3 space-y-0"
                                        >
                                          <FormControl>
                                            <Checkbox
                                              checked={field.value?.includes(
                                                option.value
                                              )}
                                              onCheckedChange={(checked) => {
                                                return checked
                                                  ? field.onChange([
                                                      ...(field.value || []),
                                                      option.value,
                                                    ])
                                                  : field.onChange(
                                                      (
                                                        field.value || []
                                                      ).filter(
                                                        (value: string) =>
                                                          value !== option.value
                                                      )
                                                    );
                                              }}
                                            />
                                          </FormControl>
                                          <FormLabel className="font-normal">
                                            {option.label}
                                          </FormLabel>
                                        </FormItem>
                                      );
                                    }}
                                  />
                                )
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="parameters.ZPL.couleur_cible"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Couleur de la cible</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selectionner la couleur de la cible" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {ReeducationsOptions.ZPL.parametre.couleur_cible?.map(
                                    (option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="parameters.ZPL.couleur_fond"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Couleur du fond</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selectionner la couleur du fond" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {ReeducationsOptions.ZPL.parametre.couleur_fond?.map(
                                    (option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="parameters.ZPL.mouvement"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Mouvement des cibles</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selectionner le mouvement des cibles" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {ReeducationsOptions.ZPL.parametre.mouvement?.map(
                                    (option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="parameters.ZPL.nombre_par_hemichamp"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                Nombre de cible par hémichamp
                              </FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selectionner le nombre de cibles" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {ReeducationsOptions.ZPL.parametre.nombre_par_hemichamp?.map(
                                    (option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <DialogFooter className="sm:justify-start">
                        <DialogClose asChild>
                          <Button type="button" variant="secondary" onClick={() => {form.setValue("parameters.ZPL.selected", true)}}>
                            Enregistrer
                          </Button>
                        </DialogClose>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </TableCell>
                <TableCell>
                  <FormField
                    control={form.control}
                    name="parameters.ZPL.zt"
                    render={({ field }) => (
                      <FormItem>
                        <div className="text-left ml-1">
                          <FormLabel>Selection des zones à cibler</FormLabel>
                        </div>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selectionner la zone de travail" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {selections.map((option) => (
                              <SelectItem
                                key={option.timeline}
                                value={option.timeline}
                              >
                                {option.timeline}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </TableCell>
              </TableRow>
              <TableRow className="bg-module-champ-row">
                <TableCell className="font-medium">
                  <FormField
                    name="parameters.ZPI.selected"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  ></FormField>
                </TableCell>
                {/* <TableCell className="text-left">
                {ReeducationsOptions.ZPI.module}
              </TableCell> */}
                <TableCell className="text-left">
                  {ReeducationsOptions.ZPI.sous_module}
                </TableCell>
                <TableCell className="text-left">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline">
                        <span className="sr-only">Modifier</span>
                        <Wrench className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>
                          {ReeducationsOptions.ZPI.sous_module}
                        </DialogTitle>
                        <DialogDescription>
                          Paramétrage de l'exercice
                        </DialogDescription>
                      </DialogHeader>
                      <div className="flex flex-col items-left space-x-2">
                        <FormField
                          control={form.control}
                          name="parameters.ZPI.duree"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Durée</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selectionner la durée" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {ReeducationsOptions.ZPI.parametre.duree?.map(
                                    (option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="parameters.ZPI.taille"
                          render={() => (
                            <FormItem>
                              <FormLabel>Taille de la cible</FormLabel>
                              {ReeducationsOptions.ZPI.parametre.taille?.map(
                                (option) => (
                                  <FormField
                                    key={option.value}
                                    control={form.control}
                                    name="parameters.ZPI.taille"
                                    render={({ field }) => {
                                      return (
                                        <FormItem
                                          key={option.value}
                                          className="flex flex-row items-start space-x-3 space-y-0"
                                        >
                                          <FormControl>
                                            <Checkbox
                                              checked={field.value?.includes(
                                                option.value
                                              )}
                                              onCheckedChange={(checked) => {
                                                return checked
                                                  ? field.onChange([
                                                      ...(field.value || []),
                                                      option.value,
                                                    ])
                                                  : field.onChange(
                                                      (
                                                        field.value || []
                                                      ).filter(
                                                        (value: string) =>
                                                          value !== option.value
                                                      )
                                                    );
                                              }}
                                            />
                                          </FormControl>
                                          <FormLabel className="font-normal">
                                            {option.label}
                                          </FormLabel>
                                        </FormItem>
                                      );
                                    }}
                                  />
                                )
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="parameters.ZPI.couleur_cible"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Couleur de la cible</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selectionner la couleur de la cible" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {ReeducationsOptions.ZPI.parametre.couleur_cible?.map(
                                    (option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="parameters.ZPI.couleur_fond"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Couleur du fond</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selectionner la couleur du fond" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {ReeducationsOptions.ZPI.parametre.couleur_fond?.map(
                                    (option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="parameters.ZPI.type_forme"
                          render={() => (
                            <FormItem>
                              <FormLabel>Type de formes</FormLabel>
                              {ReeducationsOptions.ZPI.parametre.type_forme?.map(
                                (option) => (
                                  <FormField
                                    key={option.value}
                                    control={form.control}
                                    name="parameters.ZPI.type_forme"
                                    render={({ field }) => {
                                      return (
                                        <FormItem
                                          key={option.value}
                                          className="flex flex-row items-start space-x-3 space-y-0"
                                        >
                                          <FormControl>
                                            <Checkbox
                                              checked={field.value?.includes(
                                                option.value
                                              )}
                                              onCheckedChange={(checked) => {
                                                return checked
                                                  ? field.onChange([
                                                      ...(field.value || []),
                                                      option.value,
                                                    ])
                                                  : field.onChange(
                                                      (
                                                        field.value || []
                                                      ).filter(
                                                        (value: string) =>
                                                          value !== option.value
                                                      )
                                                    );
                                              }}
                                            />
                                          </FormControl>
                                          <FormLabel className="font-normal">
                                            {option.label}
                                          </FormLabel>
                                        </FormItem>
                                      );
                                    }}
                                  />
                                )
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="parameters.ZPI.mouvement"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Mouvement des cibles</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selectionner le mouvement des cibles" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {ReeducationsOptions.ZPI.parametre.mouvement?.map(
                                    (option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="parameters.ZPI.nombre_par_hemichamp"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                Nombre de cible par hémichamp
                              </FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selectionner le nombre de cibles" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {ReeducationsOptions.ZPI.parametre.nombre_par_hemichamp?.map(
                                    (option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <DialogFooter className="sm:justify-start">
                        <DialogClose asChild>
                          <Button type="button" variant="secondary" onClick={() => {form.setValue("parameters.ZPI.selected", true)}}>
                            Enregistrer
                          </Button>
                        </DialogClose>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </TableCell>
                <TableCell>
                  <FormField
                    control={form.control}
                    name="parameters.ZPI.zt"
                    render={({ field }) => (
                      <FormItem>
                        <div className="text-left ml-1">
                          <FormLabel>Selection des zones à cibler</FormLabel>
                        </div>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selectionner la zone de travail" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {selections.map((option) => (
                              <SelectItem
                                key={option.timeline}
                                value={option.timeline}
                              >
                                {option.timeline}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </TableCell>
              </TableRow>
              <TableRow className="bg-module-champ-row">
                <TableCell className="font-medium">
                  <FormField
                    name="parameters.ZPCCF.selected"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  ></FormField>
                </TableCell>
                {/* <TableCell className="text-left">
                {ReeducationsOptions.ZPCCF.module}
              </TableCell> */}
                <TableCell className="text-left">
                  {ReeducationsOptions.ZPCCF.sous_module}
                </TableCell>
                <TableCell className="text-left">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline">
                        <span className="sr-only">Modifier</span>
                        <Wrench className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>
                          {ReeducationsOptions.ZPCCF.sous_module}
                        </DialogTitle>
                        <DialogDescription>
                          Paramétrage de l'exercice
                        </DialogDescription>
                      </DialogHeader>
                      <div className="flex flex-col items-left space-x-2">
                        <FormField
                          control={form.control}
                          name="parameters.ZPCCF.duree"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Durée</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selectionner la durée" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {ReeducationsOptions.ZPCCF.parametre.duree?.map(
                                    (option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="parameters.ZPCCF.taille"
                          render={() => (
                            <FormItem>
                              <FormLabel>Taille de la cible</FormLabel>
                              {ReeducationsOptions.ZPCCF.parametre.taille?.map(
                                (option) => (
                                  <FormField
                                    key={option.value}
                                    control={form.control}
                                    name="parameters.ZPCCF.taille"
                                    render={({ field }) => {
                                      return (
                                        <FormItem
                                          key={option.value}
                                          className="flex flex-row items-start space-x-3 space-y-0"
                                        >
                                          <FormControl>
                                            <Checkbox
                                              checked={field.value?.includes(
                                                option.value
                                              )}
                                              onCheckedChange={(checked) => {
                                                return checked
                                                  ? field.onChange([
                                                      ...(field.value || []),
                                                      option.value,
                                                    ])
                                                  : field.onChange(
                                                      (
                                                        field.value || []
                                                      ).filter(
                                                        (value: string) =>
                                                          value !== option.value
                                                      )
                                                    );
                                              }}
                                            />
                                          </FormControl>
                                          <FormLabel className="font-normal">
                                            {option.label}
                                          </FormLabel>
                                        </FormItem>
                                      );
                                    }}
                                  />
                                )
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="parameters.ZPCCF.mouvement"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{TitreChamp.mouvement}</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selectionner le Mouvement des cibles" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {ReeducationsOptions.ZPCCF.parametre.mouvement?.map(
                                    (option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        {/* <FormField
                        control={form.control}
                        name="parameters.ZPCCF.side_aveugle"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{TitreChamp.side_aveugle}</FormLabel>
                            <Select
                              onValueChange={field.onChange}
                              defaultValue={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Sélectionner" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {ReeducationsOptions.ZPCCF.parametre.side_aveugle?.map(
                                  (option) => (
                                    <SelectItem
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </SelectItem>
                                  )
                                )}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      /> */}
                      </div>
                      <DialogFooter className="sm:justify-start">
                        <DialogClose asChild>
                          <Button type="button" variant="secondary" onClick={() => {form.setValue("parameters.ZPCCF.selected", true)}}>
                            Enregistrer
                          </Button>
                        </DialogClose>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </TableCell>
                <TableCell>
                  <FormField
                    control={form.control}
                    name="parameters.ZPCCF.zt"
                    render={({ field }) => (
                      <FormItem>
                        <div className="text-left ml-1">
                          <FormLabel>Selection des zones à cibler</FormLabel>
                        </div>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selectionner la zone de travail" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {selections.map((option) => (
                              <SelectItem
                                key={option.timeline}
                                value={option.timeline}
                              >
                                {option.timeline}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </TableCell>
              </TableRow>
              <TableRow className="bg-module-champ-row">
                <TableCell className="font-medium">
                  <FormField
                    name="parameters.ZPFCF.selected"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  ></FormField>
                </TableCell>
                {/* <TableCell className="text-left">
                {ReeducationsOptions.ZPFCF.module}
              </TableCell> */}
                <TableCell className="text-left">
                  {ReeducationsOptions.ZPFCF.sous_module}
                </TableCell>
                <TableCell className="text-left">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline">
                        <span className="sr-only">Modifier</span>
                        <Wrench className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>
                          {ReeducationsOptions.ZPFCF.sous_module}
                        </DialogTitle>
                        <DialogDescription>
                          Paramétrage de l'exercice
                        </DialogDescription>
                      </DialogHeader>
                      <div className="flex flex-col items-left space-x-2">
                        <FormField
                          control={form.control}
                          name="parameters.ZPFCF.duree"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Durée</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selectionner la durée" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {ReeducationsOptions.ZPFCF.parametre.duree?.map(
                                    (option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="parameters.ZPFCF.taille"
                          render={() => (
                            <FormItem>
                              <FormLabel>Taille de la cible</FormLabel>
                              {ReeducationsOptions.ZPFCF.parametre.taille?.map(
                                (option) => (
                                  <FormField
                                    key={option.value}
                                    control={form.control}
                                    name="parameters.ZPFCF.taille"
                                    render={({ field }) => {
                                      return (
                                        <FormItem
                                          key={option.value}
                                          className="flex flex-row items-start space-x-3 space-y-0"
                                        >
                                          <FormControl>
                                            <Checkbox
                                              checked={field.value?.includes(
                                                option.value
                                              )}
                                              onCheckedChange={(checked) => {
                                                return checked
                                                  ? field.onChange([
                                                      ...(field.value || []),
                                                      option.value,
                                                    ])
                                                  : field.onChange(
                                                      (
                                                        field.value || []
                                                      ).filter(
                                                        (value: string) =>
                                                          value !== option.value
                                                      )
                                                    );
                                              }}
                                            />
                                          </FormControl>
                                          <FormLabel className="font-normal">
                                            {option.label}
                                          </FormLabel>
                                        </FormItem>
                                      );
                                    }}
                                  />
                                )
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                                                <FormField
                          control={form.control}
                          name="parameters.ZPFCF.couleur_cible"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Couleur de la cible</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selectionner la couleur de la cible" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {ReeducationsOptions.ZPFCF.parametre.couleur_cible?.map(
                                    (option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="parameters.ZPFCF.couleur_fond"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Couleur du fond</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selectionner la couleur du fond" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {ReeducationsOptions.ZPFCF.parametre.couleur_fond?.map(
                                    (option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="parameters.ZPFCF.type_forme"
                          render={() => (
                            <FormItem>
                              <FormLabel>Type de formes</FormLabel>
                              {ReeducationsOptions.ZPFCF.parametre.type_forme?.map(
                                (option) => (
                                  <FormField
                                    key={option.value}
                                    control={form.control}
                                    name="parameters.ZPFCF.type_forme"
                                    render={({ field }) => {
                                      return (
                                        <FormItem
                                          key={option.value}
                                          className="flex flex-row items-start space-x-3 space-y-0"
                                        >
                                          <FormControl>
                                            <Checkbox
                                              checked={field.value?.includes(
                                                option.value
                                              )}
                                              onCheckedChange={(checked) => {
                                                return checked
                                                  ? field.onChange([
                                                      ...(field.value || []),
                                                      option.value,
                                                    ])
                                                  : field.onChange(
                                                      (
                                                        field.value || []
                                                      ).filter(
                                                        (value: string) =>
                                                          value !== option.value
                                                      )
                                                    );
                                              }}
                                            />
                                          </FormControl>
                                          <FormLabel className="font-normal">
                                            {option.label}
                                          </FormLabel>
                                        </FormItem>
                                      );
                                    }}
                                  />
                                )
                              )}
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="parameters.ZPFCF.mouvement"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{TitreChamp.mouvement}</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selectionner le Mouvement des cibles" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {ReeducationsOptions.ZPFCF.parametre.mouvement?.map(
                                    (option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <DialogFooter className="sm:justify-start">
                        <DialogClose asChild>
                          <Button type="button" variant="secondary" onClick={() => {form.setValue("parameters.ZPFCF.selected", true)}}>
                            Enregistrer
                          </Button>
                        </DialogClose>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </TableCell>
                <TableCell>
                  <FormField
                    control={form.control}
                    name="parameters.ZPFCF.zt"
                    render={({ field }) => (
                      <FormItem>
                        <div className="text-left ml-1">
                          <FormLabel>Selection des zones à cibler</FormLabel>
                        </div>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selectionner la zone de travail" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {selections.map((option) => (
                              <SelectItem
                                key={option.timeline}
                                value={option.timeline}
                              >
                                {option.timeline}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </TableCell>
              </TableRow>
              <TableRow className="h-10">
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>
              <TableRow className="bg-module-poursuite-header text-white hover:bg-module-poursuite-header/70">
                <TableCell>
                  <strong>Poursuite Visuelle</strong>
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>
              <TableRow className="bg-module-poursuite-row">
                <TableCell className="font-medium">
                  <FormField
                    name="parameters.PVS.selected"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  ></FormField>
                </TableCell>
                {/* <TableCell className="text-left">
                {ReeducationsOptions.PVS.module}
              </TableCell> */}
                <TableCell className="text-left">
                  {ReeducationsOptions.PVS.sous_module}
                </TableCell>
                <TableCell className="text-left">
                  {" "}
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline">
                        <span className="sr-only">Modifier</span>
                        <Wrench className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>
                          {ReeducationsOptions.PVS.sous_module}
                        </DialogTitle>
                        <DialogDescription>
                          Paramétrage de l'exercice
                        </DialogDescription>
                      </DialogHeader>
                      <div className="flex flex-col items-left space-x-2">
                        <FormField
                          control={form.control}
                          name="parameters.PVS.vitesse"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{TitreChamp.vitesse}</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selectionner" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {ReeducationsOptions.PVS.parametre.vitesse?.map(
                                    (option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="parameters.PVS.duree"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{TitreChamp.duree}</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selectionner" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {ReeducationsOptions.PVS.parametre.duree?.map(
                                    (option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <DialogFooter className="sm:justify-start">
                        <DialogClose asChild>
                          <Button type="button" variant="secondary" onClick={() => {form.setValue("parameters.PVS.selected", true)}}>
                            Enregistrer
                          </Button>
                        </DialogClose>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </TableCell>
                <TableCell />
              </TableRow>
              <TableRow className="h-10">
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>
              <TableRow className="bg-module-optocinetique-header text-white hover:bg-module-optocinetique-header/70">
                <TableCell>
                  <strong>Optocinétique</strong>
                </TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
                <TableCell></TableCell>
              </TableRow>
              <TableRow className="bg-module-optocinetique-row">
                <TableCell className="font-medium">
                  <FormField
                    name="parameters.OT.selected"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  ></FormField>
                </TableCell>
                {/* <TableCell className="text-left">
                {ReeducationsOptions.OT.module}
              </TableCell> */}
                <TableCell className="text-left">
                  {ReeducationsOptions.OT.sous_module}
                </TableCell>
                <TableCell className="text-left">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline">
                        <span className="sr-only">Modifier</span>
                        <Wrench className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>
                          {ReeducationsOptions.OT.sous_module}
                        </DialogTitle>
                        <DialogDescription>
                          Paramétrage de l'exercice
                        </DialogDescription>
                      </DialogHeader>
                      <div className="flex flex-col items-left space-x-2">
                        <FormField
                          control={form.control}
                          name="parameters.OT.vitesse"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{TitreChamp.vitesse}</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selectionner" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {ReeducationsOptions.OT.parametre.vitesse?.map(
                                    (option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="parameters.OT.duree"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{TitreChamp.duree}</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selectionner" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {ReeducationsOptions.OT.parametre.duree?.map(
                                    (option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="parameters.OT.contenu"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{TitreChamp.contenu}</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selectionner" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {["OT"].map((moduleKey: string) => {
                                    const module =
                                      ReeducationsOptions[
                                        moduleKey as keyof typeof ReeducationsOptions
                                      ];
                                    return module.parametre?.contenu?.map(
                                      (difficulty: TextDifficulty) => (
                                        <div key={difficulty.value}>
                                          <div className="px-3 py-1 font-medium text-gray-500">
                                            {difficulty.label}
                                          </div>
                                          {difficulty.textes.map(
                                            (text: string) => (
                                              <SelectItem
                                                key={text}
                                                value={text}
                                              >
                                                {text}
                                              </SelectItem>
                                            )
                                          )}
                                        </div>
                                      )
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <DialogFooter className="sm:justify-start">
                        <DialogClose asChild>
                          <Button type="button" variant="secondary" onClick={() => {form.setValue("parameters.OT.selected", true)}}>
                            Enregistrer
                          </Button>
                        </DialogClose>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </TableCell>
                <TableCell />
              </TableRow>
              <TableRow className="bg-module-optocinetique-row">
                <TableCell className="font-medium">
                  <FormField
                    name="parameters.OB.selected"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  ></FormField>
                </TableCell>
                {/* <TableCell className="text-left">
                {ReeducationsOptions.OB.module}
              </TableCell> */}
                <TableCell className="text-left">
                  {ReeducationsOptions.OB.sous_module}
                </TableCell>
                <TableCell className="text-left">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline">
                        <span className="sr-only">Modifier</span>
                        <Wrench className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>
                          {ReeducationsOptions.OB.sous_module}
                        </DialogTitle>
                        <DialogDescription>
                          Paramétrage de l'exercice
                        </DialogDescription>
                      </DialogHeader>
                      <div className="flex flex-col items-left space-x-2">
                        <FormField
                          control={form.control}
                          name="parameters.OB.vitesse"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{TitreChamp.vitesse}</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selectionner" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {ReeducationsOptions.OB.parametre.vitesse?.map(
                                    (option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="parameters.OB.duree"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{TitreChamp.duree}</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selectionner" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {ReeducationsOptions.OB.parametre.duree?.map(
                                    (option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    )
                                  )}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="parameters.OB.contenu"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>{TitreChamp.contenu}</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Selectionner" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {/* Loop over all difficulties in both OT and OB */}
                                  {["OB"].map((moduleKey: string) => {
                                    const module =
                                      ReeducationsOptions[
                                        moduleKey as keyof typeof ReeducationsOptions
                                      ];
                                    return module.parametre?.contenu?.map(
                                      (difficulty) => (
                                        <div key={difficulty.value}>
                                          {/* Group label for each difficulty */}
                                          <div className="px-3 py-1 font-medium text-gray-500">
                                            {difficulty.label}
                                          </div>
                                          {/* Map each text inside the difficulty */}
                                          {difficulty.textes.map(
                                            (text: string) => (
                                              <SelectItem
                                                key={text}
                                                value={text}
                                              >
                                                {text}
                                              </SelectItem>
                                            )
                                          )}
                                        </div>
                                      )
                                    );
                                  })}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                      <DialogFooter className="sm:justify-start">
                        <DialogClose asChild>
                          <Button type="button" variant="secondary" onClick={() => {form.setValue("parameters.OB.selected", true)}}>
                            Enregistrer
                          </Button>
                        </DialogClose>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem className="w-3/4 ml-4 mt-6">
                <div className="text-left ml-11">
                  <FormLabel>Notes</FormLabel>
                </div>
                <FormControl>
                  <Textarea
                    placeholder="Ajouter un commentaire à la séance"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            className="mt-6"
            onClick={() => {
              // console.log(form.formState.errors);
            }}
          >
            <Save className="mr-2" />
            Valider la séance
          </Button>
        </form>
      </Form>
    </Card>
  );
}
